import { AxiosService } from '../../axios/axios.service';
import { tAxiosCustomConfig } from '../../axios/axios.types.ts';
import { EXPIRES_AFTER_UNIT } from './Pipe.constants';
import {
  tCampaignRecipientPreviewResponse,
  tCreateCampaignLeadsRequest,
  tCreateCampaignLeadsResponse,
  tCreateCampaignStageRequest,
  tCreateCampaignStageResponse,
  tCreateFunnelRequest,
  tCreateLeadRequest,
  tFetchPredefinedVariablesResponse,
  tFetchTagsResponse,
  tFetchTemplatesResponse,
  tFunnel,
  tFunnelLeadLegacy,
  tFunnelLegacy,
  tFunnelsWithStages,
  tGetLeadsQueryParams,
  tGetMoreLeadsResponse,
  tPipeResponse,
  tRequestChat,
  tRequestChatResponse,
  tUpdateFunnelRequest,
  tUpdateLeadRequest,
  tUpdateLeadsInBulkRequest,
  tWorkspaceFunnel,
} from './Pipe.types';

export const PipeService = {
  getWorkspaceFunnels: async function (
    workspaceUuid: string,
  ): Promise<tPipeResponse<{ pipes: tWorkspaceFunnel[] }>> {
    try {
      const response = await AxiosService.get<
        tPipeResponse<{ pipes: tWorkspaceFunnel[] }>
      >(`workspace/${workspaceUuid}/pipes`);

      if (response.data.pipes === null)
        throw new Error('No se encontraron embudos para este workspace');

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getFunnelInfo: async function (
    workspaceId: string,
    funnelUuid: string,
  ): Promise<tPipeResponse<tFunnel>> {
    try {
      const response = await AxiosService.get<tPipeResponse<tFunnel>>(
        `/workspace/${workspaceId}/pipe/${funnelUuid}`,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getWorkspaceFunnelsWithStages: async function (
    workspaceId: string,
  ): Promise<tPipeResponse<{ pipes: tFunnelsWithStages[] }>> {
    try {
      const response = await AxiosService.get<
        tPipeResponse<{ pipes: tFunnelsWithStages[] }>
      >(`/workspace/${workspaceId}/pipes/stages`);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getMoreLeads: async function (
    workspaceId: string,
    queryParams?: tGetLeadsQueryParams,
  ): Promise<tPipeResponse<tGetMoreLeadsResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        params: queryParams,
      };

      const response = await AxiosService.get<
        tPipeResponse<tGetMoreLeadsResponse>
      >(`/workspace/${workspaceId}/leads/filter`, config);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getUnclassifiedLeads: async function (
    workspaceId: string,
    funnelId: string,
    page: number,
  ): Promise<tPipeResponse<tGetMoreLeadsResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        params: { page, paginate: 25 },
        isLegacy: true,
      };

      const response = await AxiosService.get<
        tPipeResponse<tGetMoreLeadsResponse>
      >(`/leads/ws/${workspaceId}/fnl/${funnelId}/unclassified-leads`, config);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getLeadsBySearch: async function (
    currentWorkspaceId: string,
    queryParams?: tGetLeadsQueryParams,
  ): Promise<tPipeResponse<tGetMoreLeadsResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        params: queryParams,
      };

      const response = await AxiosService.get<
        tPipeResponse<tGetMoreLeadsResponse>
      >(`/workspace/${currentWorkspaceId}/leads/search`, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  createFunnel: async function (
    workspaceId: string,
    userId: string,
    request: tCreateFunnelRequest,
  ): Promise<tPipeResponse<tFunnelLegacy>> {
    try {
      const config: tAxiosCustomConfig = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      const response = await AxiosService.post<
        tCreateFunnelRequest,
        tPipeResponse<tFunnelLegacy>
      >(`/leads/ws/${workspaceId}/fnl`, request, config);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createLead: async function (
    workspaceId: string,
    userId: string,
    request: tCreateLeadRequest,
  ): Promise<tPipeResponse<tFunnelLeadLegacy>> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      const response = await AxiosService.post<
        tCreateLeadRequest,
        tPipeResponse<tFunnelLeadLegacy>
      >(`/leads/ws/${workspaceId}/lds`, request, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createIntegrationLead: async function (
    userId: string,
    request: tRequestChat,
  ): Promise<tRequestChatResponse> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      const response = await AxiosService.post<
        tRequestChat,
        tRequestChatResponse
      >(`/chats/chats`, request, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * The single request builder behind the funnel `PUT`. Every funnel edit in
   * the app — rename, drag, stage add/delete, Lead Agent config, threshold —
   * goes through here, which is why KTD10's unit marker is attached here and
   * nowhere else: one line marks every write this bundle makes, so the backend
   * can tell a post-migration payload (minutes) from a stale tab still
   * resubmitting hours. Callers never pass `expires_after_unit`; it is
   * overwritten here on purpose.
   */
  updateFunnel: async function (
    workspaceId: string,
    funnelId: string,
    userId: string,
    request: tUpdateFunnelRequest,
  ): Promise<tPipeResponse<tFunnelLegacy>> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };
      const markedRequest: tUpdateFunnelRequest = {
        ...request,
        expires_after_unit: EXPIRES_AFTER_UNIT,
      };
      const response = await AxiosService.put<
        tUpdateFunnelRequest,
        tPipeResponse<tFunnelLegacy>
      >(`/leads/ws/${workspaceId}/fnl/${funnelId}`, markedRequest, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateLead: async function (
    workspaceId: string,
    leadId: string,
    userId: string,
    request: tUpdateLeadRequest,
  ): Promise<tPipeResponse<tFunnelLeadLegacy>> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };
      const response = await AxiosService.put<
        tUpdateLeadRequest,
        tPipeResponse<tFunnelLeadLegacy>
      >(`/leads/ws/${workspaceId}/lds/${leadId}`, request, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateLeadsInBulk: async function (
    workspaceId: string,
    userId: string,
    request: tUpdateLeadsInBulkRequest,
  ): Promise<tPipeResponse<tFunnelLeadLegacy>> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };
      const response = await AxiosService.put<
        tUpdateLeadsInBulkRequest,
        tPipeResponse<tFunnelLeadLegacy>
      >(`/leads/ws/${workspaceId}/lds/bulk-edit`, request, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  deleteFunnel: async function (
    workspaceId: string,
    funnelId: string,
    userId: string,
    request: tUpdateLeadsInBulkRequest,
  ): Promise<void> {
    try {
      const options = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      await AxiosService.deleteBulkById<
        tUpdateLeadsInBulkRequest,
        tUpdateFunnelRequest
      >(`/leads/ws/${workspaceId}/fnl/${funnelId}`, request, options);

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Method to fetch and store templates
  fetchTemplates: async (
    workspaceId: string,
  ): Promise<tFetchTemplatesResponse> => {
    try {
      const options = {
        isLegacy: true,
      };

      const response = await AxiosService.post<
        // eslint-disable-next-line @typescript-eslint/no-empty-object-type
        {},
        tFetchTemplatesResponse
      >(`/whatsapp-api/meta/${workspaceId}/templates/fetch`, {}, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Method to get templates
  getTemplates: async (
    workspaceId: string,
    handle?: string,
  ): Promise<tFetchTemplatesResponse> => {
    const options: tAxiosCustomConfig = {
      isLegacy: true,
      ...(handle && { params: { handle } }),
    };

    try {
      const response = await AxiosService.get<tFetchTemplatesResponse>(
        `/whatsapp-api/meta/${workspaceId}/templates`,
        options,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getTags: async (workspaceId: string): Promise<tFetchTagsResponse> => {
    const options = {
      isLegacy: true,
    };

    try {
      const response = await AxiosService.get<tFetchTagsResponse>(
        `/leads/ws/${workspaceId}/tags`,
        options,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createCampaignStage: async (
    workspaceId: string,
    request: tCreateCampaignStageRequest,
  ): Promise<tCreateCampaignStageResponse> => {
    try {
      const options = {
        isLegacy: true,
      };
      const response = await AxiosService.post<
        tCreateCampaignStageRequest,
        tCreateCampaignStageResponse
      >(
        `/whatsapp-api/campaigns/${workspaceId}/stage/create`,
        request,
        options,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Pre-send truth for the bulk modal (BUG-569): real recipient count and
   * credit availability computed by WBS with the exact send-path predicate.
   * May 404 or return partial fields on an older WBS — callers must treat any
   * failure as "preview unavailable" and fall back to the stage counter.
   */
  getCampaignStageRecipientPreview: async (
    workspaceId: string,
    stageUuid: string,
  ): Promise<tCampaignRecipientPreviewResponse> => {
    try {
      const options: tAxiosCustomConfig = {
        isLegacy: true,
        // Bound the modal's loading state: the send action is disabled while
        // this request is in flight, so a hung request must resolve into the
        // fallback path instead of blocking the user.
        timeout: 8000,
      };

      const response =
        await AxiosService.get<tCampaignRecipientPreviewResponse>(
          `/whatsapp-api/campaigns/${workspaceId}/stage/${stageUuid}/recipients-preview`,
          options,
        );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createCampaignLeads: async (
    workspaceId: string,
    request: tCreateCampaignLeadsRequest,
  ): Promise<tCreateCampaignLeadsResponse> => {
    try {
      const options = {
        isLegacy: true,
      };

      const response = await AxiosService.post<
        tCreateCampaignLeadsRequest,
        tCreateCampaignLeadsResponse
      >(`/whatsapp-api/campaigns/${workspaceId}/list/create`, request, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getPredefinedVariables: async (): Promise<tFetchPredefinedVariablesResponse> => {
    const options: tAxiosCustomConfig = {
      isLegacy: true,
    };

    const response = await AxiosService.get<tFetchPredefinedVariablesResponse>(
      '/whatsapp-api/templates/variables',
      options,
    );

    return response;
  },
};
